// apps/web/src/components/vendas/roteiro-combobox.tsx — Fase 12, Plano 07 (PV-01, D-05).
// Seletor de "Processo / roteiro" com busca no servidor (mesmo padrão de cliente-combobox.tsx).
// Consulta GET /api/v1/vendas/lookups/roteiros (pv:ler; até 20 roteiros com versão vigente hoje).
import { useEffect, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, Search, X } from 'lucide-react';
import type { RoteiroLookupPv } from '@pieltec/contracts';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

interface RoteiroComboboxProps {
  /** `roteiroId` selecionado (null = sem roteiro; a pendência de roteiro é derivada pelo servidor). */
  readonly value: string | null;
  readonly onChange: (roteiroId: string | null) => void;
  readonly disabled?: boolean;
  readonly id?: string;
}

const ATRASO_BUSCA_MS = 250;

export function RoteiroCombobox({ value, onChange, disabled = false, id }: RoteiroComboboxProps) {
  const [open, setOpen] = useState(false);
  const [busca, setBusca] = useState('');
  const [roteiros, setRoteiros] = useState<readonly RoteiroLookupPv[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const buscaRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    let ativo = true;
    setCarregando(true);
    setErro(null);
    const timer = window.setTimeout(() => {
      const termo = busca.trim();
      const query = termo ? `?busca=${encodeURIComponent(termo)}` : '';
      void fetch(`/api/v1/vendas/lookups/roteiros${query}`, { credentials: 'include' })
        .then(async (res) => {
          if (!ativo) return;
          if (!res.ok) {
            setErro('Não foi possível carregar os roteiros.');
            setRoteiros([]);
            return;
          }
          setRoteiros((await res.json()) as RoteiroLookupPv[]);
        })
        .catch(() => {
          if (ativo) setErro('Não foi possível carregar os roteiros.');
        })
        .finally(() => {
          if (ativo) setCarregando(false);
        });
    }, ATRASO_BUSCA_MS);
    return () => {
      ativo = false;
      window.clearTimeout(timer);
    };
  }, [open, busca]);

  return (
    <Popover
      open={open}
      onOpenChange={(proximo) => {
        setOpen(proximo);
        if (!proximo) setBusca('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="listbox"
          className="ui-control w-full flex items-center justify-between min-h-[var(--control-height,2rem)] px-3 py-2 bg-white border border-slate-300 rounded-md text-sm text-slate-800 disabled:opacity-50"
        >
          <span className="truncate">{value ?? 'Selecione o roteiro...'}</span>
          <ChevronsUpDown className="w-4 h-4 text-slate-400 shrink-0 ml-2" aria-hidden="true" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="p-0 overflow-hidden"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          buscaRef.current?.focus();
        }}
      >
        <div className="p-2 border-b bg-slate-50 flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" aria-hidden="true" />
          <input
            ref={buscaRef}
            type="text"
            aria-label="Buscar roteiro"
            placeholder="Buscar roteiro"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="w-full bg-transparent text-sm text-slate-900 focus:outline-none"
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1" role="listbox" aria-label="Roteiros">
          {value && (
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setOpen(false);
                setBusca('');
              }}
              className="w-full min-h-[var(--control-height,2rem)] flex items-center gap-2 px-3 py-2 text-left text-sm rounded-md text-slate-600 hover:bg-slate-100"
            >
              <X className="w-4 h-4 shrink-0" aria-hidden="true" />
              Limpar roteiro
            </button>
          )}
          {carregando ? (
            <div className="p-4 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              Carregando roteiros...
            </div>
          ) : erro ? (
            <div role="alert" className="p-4 text-center text-sm text-rose-700">
              {erro}
            </div>
          ) : roteiros.length === 0 ? (
            <div className="p-4 text-center text-sm text-slate-500">Nenhum roteiro vigente encontrado.</div>
          ) : (
            roteiros.map((roteiro) => {
              const selecionado = value === roteiro.roteiroId;
              return (
                <button
                  key={roteiro.roteiroId}
                  type="button"
                  role="option"
                  aria-selected={selecionado}
                  onClick={() => {
                    onChange(roteiro.roteiroId);
                    setOpen(false);
                    setBusca('');
                  }}
                  className={`w-full min-h-[var(--control-height,2rem)] flex items-center justify-between px-3 py-2 text-left text-sm rounded-md transition-colors ${
                    selecionado ? 'bg-slate-900 text-white font-semibold' : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <span className="flex flex-col truncate pr-2">
                    <span className="truncate">{roteiro.roteiroId}</span>
                    {roteiro.descricao ? <span className={`truncate text-xs ${selecionado ? 'text-slate-300' : 'text-slate-500'}`}>{roteiro.descricao}</span> : null}
                    <span className={`text-xs ${selecionado ? 'text-slate-300' : 'text-slate-500'}`}>versão {roteiro.versaoVigente}</span>
                  </span>
                  {selecionado && <Check className="w-4 h-4 shrink-0" aria-hidden="true" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
